import React, { useEffect, useState } from 'react';
import SearchableSelect from '../common/SearchableSelect';
import { CostCentreService } from '../../services/costCentre.service';
import { DepartmentOption } from '../../interfaces/costCentre.interface';

/** One fetch per page load shared by every picker on it (a journal grid renders one per line). */
let cache: { institution: string; promise: Promise<DepartmentOption[]> } | null = null;

export const loadDepartmentOptions = (institution: string): Promise<DepartmentOption[]> => {
    if (!cache || cache.institution !== institution) {
        const promise = CostCentreService.getDepartments().catch(() => {
            cache = null; // let the next mount retry
            return [] as DepartmentOption[];
        });
        cache = { institution, promise };
    }
    return cache.promise;
};

export const departmentLabel = (d: DepartmentOption): string =>
    d.costCentreCode ? `${d.name} (${d.costCentreCode})` : d.name;

/** Display name of a tagged department; '—' when untagged, a short id while the option list is still loading. */
export const departmentNameOf = (options: DepartmentOption[], id: string | null | undefined): string =>
    id ? (options.find(d => d.id === id)?.name ?? id.slice(0, 8)) : '—';

export function useDepartmentOptions(institution: string | null | undefined): DepartmentOption[] {
    const [options, setOptions] = useState<DepartmentOption[]>([]);
    useEffect(() => {
        let live = true;
        if (!institution) {
            setOptions([]);
            return () => { live = false; };
        }
        loadDepartmentOptions(institution).then(o => { if (live) setOptions(o); });
        return () => { live = false; };
    }, [institution]);
    return options;
}

interface Props {
    value: string | null | undefined;
    onChange: (id: string) => void;
    options: DepartmentOption[];
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    disabled?: boolean;
    'data-testid'?: string;
    'data-help'?: string;
}

/** Department (cost-centre) picker: the app's SearchableSelect over the institution's departments. */
const DepartmentPicker: React.FC<Props> = ({ value, onChange, options, label, placeholder, size, disabled, ...rest }) => (
    <SearchableSelect<DepartmentOption>
        options={options}
        value={value ?? ''}
        onChange={id => onChange(id)}
        getOptionLabel={departmentLabel}
        label={label}
        placeholder={placeholder ?? '(none)'}
        size={size}
        disabled={disabled}
        data-testid={rest['data-testid']}
        {...(rest['data-help'] ? { ["data-help"]: rest['data-help'] } : {})}
    />
);

export default DepartmentPicker;
